/* FundOurKids shared runtime.
 *
 * Transcribed verbatim (constants, timings, thresholds, algorithms) from the
 * design's extracted React-ish source files:
 *   .superpowers/sdd/2026-09-01-fundourkids-shopify-theme/js/Homepage-Redesign-v2.js
 *   .superpowers/sdd/2026-09-01-fundourkids-shopify-theme/js/By-State.js
 *   .superpowers/sdd/2026-09-01-fundourkids-shopify-theme/js/The-Fears.js
 *   .superpowers/sdd/2026-09-01-fundourkids-shopify-theme/js/Research.js
 *   .superpowers/sdd/2026-09-01-fundourkids-shopify-theme/js/Shop.js
 *
 * The design is a "class Component extends DCLogic"; this file is a plain
 * ES5-compatible IIFE. Only the framework-forced mechanical changes were
 * made (instance fields -> closure vars, componentDidMount -> DOM-ready init,
 * componentWillUnmount -> a teardown registry, setState -> out of scope).
 * Everything else — timings, thresholds, rootMargins, easing, retry counts —
 * is carried across unchanged. See task-12-report.md for the full
 * behaviour-by-behaviour fidelity table.
 */
(function (window, document) {
  'use strict';

  var teardowns = [];

  function registerTeardown(fn) {
    teardowns.push(fn);
    return fn;
  }

  function teardownAll() {
    for (var i = 0; i < teardowns.length; i++) {
      try {
        teardowns[i]();
      } catch (e) {
        /* ignore teardown errors so one bad cleanup can't block the rest */
      }
    }
    teardowns = [];
  }

  var prefersReducedMotion =
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* The design re-queries matchMedia inside each initialiser rather than
   * reading a cached flag (Homepage-Redesign-v2.js:15,26,47; By-State.js:32,49),
   * so an OS motion-preference change takes effect on the next init/re-init.
   * window.FOK.prefersReducedMotion stays a boolean snapshot for callers
   * (Tasks 14-18); internal logic uses this instead. */
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ------------------------------------------------------------------ *
   * 1. Reveal — the design's runtime auto-tagger plus the By-State /
   *    The-Fears observer body (The-Fears cleanup, which additionally
   *    clears el.style.willChange).
   *    Design source: Homepage-Redesign-v2.js _revealInit.
   *
   *    An earlier brief told the implementer to SKIP the auto-tagger and
   *    author data-rv in Liquid instead. That authoring never happened, so
   *    the whole reveal system sat dormant: the CSS and observer shipped,
   *    but nothing was ever tagged and no section animated. Porting the
   *    design's walk fixes it in one place and covers every future page
   *    without per-section markup.
   *
   *    One adaptation: the design selects grid children with the literal
   *    '[style*="grid-template-columns"] > div', because its prototype
   *    carries inline styles. Our markup uses classes, so we detect grid
   *    containers by computed style instead — same intent, and it keeps
   *    working as later pages add their own grids.
   * ------------------------------------------------------------------ */
  function initReveal(root) {
    root = root || document;
    var rvTries = 0;
    var revealIo = null;
    var cancelled = false;
    var handlers = []; /* {el, fn} pairs for transitionend listeners, so teardown can remove them */

    /* Chrome and anything that animates itself must not be revealed. */
    function skip(el) {
      if (el.closest('#scrollnav, #socialbar, [data-noreveal]')) return true;
      var inline = el.getAttribute('style') || '';
      if (inline.indexOf('animation:') !== -1) return true;
      return false;
    }

    function isGridChild(el) {
      var p = el.parentElement;
      if (!p) return false;
      var cs = window.getComputedStyle(p);
      if (cs.display !== 'grid' && cs.display !== 'inline-grid') return false;
      return cs.gridTemplateColumns && cs.gridTemplateColumns !== 'none';
    }

    function run() {
      if (cancelled) return;

      if (reducedMotion()) return;

      /* Parent-wins order: containers first, then the text/media inside them,
         so a card animates as one unit rather than piecewise. */
      var order = [
        '.fok-actioncard', '.fok-productcard', 'article',
        'GRID_CHILD',
        'h1', 'h2', 'h3', 'h4', 'p', 'img', 'video'
      ];

      var tagged = [];

      /* Elements that already carry data-rv in the Liquid (the design authors
         data-rv="s" on e.g. the By State stats strip and ballot panel) must be
         collected FIRST. The auto-tagger below skips any element that already
         has the attribute — correctly, so it does not re-tag them — but nothing
         else ever added them to `tagged`, so they were never observed and sat
         at opacity:0 forever. Under prefers-reduced-motion the CSS override
         made them visible, which is why this only showed with animation on. */
      var preTagged = root.querySelectorAll('[data-rv]');
      for (var pt = 0; pt < preTagged.length; pt++) {
        if (skip(preTagged[pt])) continue;
        tagged.push(preTagged[pt]);
      }

      for (var oi = 0; oi < order.length; oi++) {
        var sel = order[oi];
        var nodes;
        if (sel === 'GRID_CHILD') {
          nodes = root.querySelectorAll('div, article, section');
        } else {
          nodes = root.querySelectorAll(sel);
        }
        for (var n = 0; n < nodes.length; n++) {
          var el = nodes[n];
          if (sel === 'GRID_CHILD' && !isGridChild(el)) continue;
          if (el.hasAttribute('data-rv')) continue;
          if (el.closest('[data-rv]')) continue;        /* ancestor leads */
          if (el.querySelector('[data-rv]')) continue;  /* descendant leads */
          if (skip(el)) continue;
          var tag = el.tagName.toLowerCase();
          var type = 'up';
          if (tag === 'img' || tag === 'video' || tag === 'article' ||
              el.className.indexOf('card') !== -1) {
            type = 's';
          }
          el.setAttribute('data-rv', type === 'up' ? '' : type);
          tagged.push(el);
        }
      }

      if (!tagged.length) {
        rvTries += 1;
        if (rvTries < 12) setTimeout(run, 200);
        return;
      }

      var counts = [];
      var counted = [];
      var list = [];

      function countFor(parent) {
        for (var ci = 0; ci < counted.length; ci++) {
          if (counted[ci] === parent) return counts[ci];
        }
        counted.push(parent);
        counts.push(0);
        return 0;
      }
      function bumpCount(parent) {
        for (var ci = 0; ci < counted.length; ci++) {
          if (counted[ci] === parent) {
            counts[ci] += 1;
            return;
          }
        }
      }

      for (var t = 0; t < tagged.length; t++) {
        var el = tagged[t];
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.92) {
          el.removeAttribute('data-rv');
          continue;
        }
        var parent = el.parentElement;
        var i = countFor(parent);
        bumpCount(parent);
        el.style.transitionDelay = Math.min(i * 80, 360) + 'ms';
        list.push(el);
      }

      var io = new IntersectionObserver(
        function (entries) {
          for (var e = 0; e < entries.length; e++) {
            var entry = entries[e];
            if (!entry.isIntersecting) continue;
            (function (elx) {
              var done = function (ev) {
                if (ev.target !== elx) return;
                elx.removeAttribute('data-rv');
                elx.classList.remove('rv-in');
                elx.style.transitionDelay = '';
                elx.style.willChange = '';
                elx.removeEventListener('transitionend', done);
              };
              elx.addEventListener('transitionend', done);
              handlers.push({ el: elx, fn: done });
            })(entry.target);
            entry.target.classList.add('rv-in');
            io.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );

      for (var l = 0; l < list.length; l++) io.observe(list[l]);
      revealIo = io;
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      if (revealIo) {
        revealIo.disconnect();
        revealIo = null;
      }
      for (var h = 0; h < handlers.length; h++) {
        handlers[h].el.removeEventListener('transitionend', handlers[h].fn);
      }
      handlers = [];
    });
    return teardown;
  }

  /* ------------------------------------------------------------------ *
   * 2. Count-up — Homepage-Redesign-v2.js _initCounters. Homepage-only in
   *    the design, but Tasks 14-18 all use the counter snippet.
   * ------------------------------------------------------------------ */
  function initCounters(root) {
    root = root || document;
    var ctTries = 0;
    var io = null;
    var cancelled = false;

    function run() {
      if (cancelled) return;
      var els = root.querySelectorAll('.roi-count');
      if (!els.length) {
        ctTries += 1;
        if (ctTries < 12) setTimeout(run, 200);
        return;
      }

      function ease(t) {
        return 1 - Math.pow(1 - t, 3);
      }

      function animate(el) {
        if (el.dataset.done) return;
        el.dataset.done = '1';
        var to = parseFloat(el.dataset.to);
        var dec = parseInt(el.dataset.decimals || '0', 10);
        var pre = el.dataset.prefix || '';
        var suf = el.dataset.suffix || '';
        var dur = 1600;
        var t0 = performance.now();

        function tick(now) {
          var p = Math.min(1, (now - t0) / dur);
          var v = (to * ease(p)).toFixed(dec);
          el.textContent = pre + v + suf;
          if (p < 1) requestAnimationFrame(tick);
        }

        el.textContent = pre + (0).toFixed(dec) + suf;
        requestAnimationFrame(tick);
      }

      io = new IntersectionObserver(
        function (entries) {
          for (var e = 0; e < entries.length; e++) {
            if (entries[e].isIntersecting) animate(entries[e].target);
          }
        },
        { threshold: 0.4 }
      );

      for (var i = 0; i < els.length; i++) io.observe(els[i]);
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      if (io) {
        io.disconnect();
        io = null;
      }
    });
    return teardown;
  }

  /* ------------------------------------------------------------------ *
   * 3. Scroll nav — Homepage-Redesign-v2.js _scrollNavInit (most complete
   *    variant: adaptive luminance sampler).
   *    Ruling 35 (permitted deviation): derive the ordered ids list from
   *    ".snav-item[data-target]" in document order instead of a hardcoded
   *    per-page "ids" array -- equivalent because each page's array is
   *    exactly its nav's targets in order.
   * ------------------------------------------------------------------ */
  function initScrollNav(root) {
    root = root || document;
    var snTries = 0;
    var cancelled = false;
    var onScroll = null;
    var update = null;
    var raf = null;
    var timers = [];

    function run() {
      if (cancelled) return;
      var nav = document.getElementById('scrollnav');
      if (!nav) {
        snTries += 1;
        if (snTries < 12) setTimeout(run, 200);
        return;
      }

      var itemNodes = nav.querySelectorAll('.snav-item');
      var items = [];
      var ids = [];
      for (var n = 0; n < itemNodes.length; n++) {
        items.push(itemNodes[n]);
        ids.push(itemNodes[n].getAttribute('data-target'));
      }

      for (var b = 0; b < items.length; b++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            var target = btn.getAttribute('data-target');
            var el = target ? document.getElementById(target) : null;
            if (el) {
              window.scrollTo({
                top: el.getBoundingClientRect().top + window.pageYOffset - 20,
                behavior: 'smooth'
              });
            }
          });
        })(items[b]);
      }

      update = function () {
        var mid = window.pageYOffset + window.innerHeight * 0.35;
        var active = 0;
        var bestTop = -Infinity;
        for (var i = 0; i < ids.length; i++) {
          var id = ids[i];
          var el = id ? document.getElementById(id) : null;
          if (!el) continue;
          var top = el.getBoundingClientRect().top + window.pageYOffset;
          if (top <= mid && top >= bestTop) {
            bestTop = top;
            active = i;
          }
        }
        for (var j = 0; j < items.length; j++) {
          items[j].setAttribute('data-active', j === active ? '1' : '0');
        }

        /* adaptive color: sample what's actually behind the active label —
         * solid colors, gradients (parsed), and images/media all handled. */
        var lbl = items[active] ? items[active].querySelector('.snav-label') : null;
        if (lbl) {
          var r = lbl.getBoundingClientRect();
          var x = Math.max(4, r.left - 8);
          var y = r.top + r.height / 2;
          lbl.style.visibility = 'hidden';
          var stack = document.elementsFromPoint(x, y);
          lbl.style.visibility = '';

          function lumOf(p) {
            return (0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2]) / 255;
          }

          var lum = 0.9;
          for (var s = 0; s < stack.length; s++) {
            var elx = stack[s];
            if (elx.closest && elx.closest('#scrollnav')) continue;
            var tag = elx.tagName;
            if (tag === 'IMG' || tag === 'VIDEO' || tag === 'CANVAS') {
              lum = 0.22;
              break;
            }
            var cs = getComputedStyle(elx);
            var bi = cs.backgroundImage;
            if (bi && bi !== 'none') {
              var cols = bi.match(/rgba?\([^)]+\)/g);
              if (cols && cols.length) {
                var sum = 0;
                var cnt = 0;
                for (var c = 0; c < cols.length; c++) {
                  var p = cols[c].match(/[\d.]+/g);
                  if (!p) continue;
                  p = p.map(Number);
                  if (p.length >= 4 && p[3] < 0.25) continue;
                  sum += lumOf(p);
                  cnt++;
                }
                if (cnt) {
                  lum = sum / cnt;
                  break;
                }
              }
              /* image url with no readable color stops -> assume dark media */
              if (/url\(/.test(bi)) {
                lum = 0.25;
                break;
              }
            }
            var m = cs.backgroundColor.match(/rgba?\(([^)]+)\)/);
            if (!m) continue;
            var pc = m[1].split(',').map(function (v) {
              return parseFloat(v);
            });
            if (pc.length >= 4 && pc[3] < 0.3) continue;
            lum = lumOf(pc);
            break;
          }
          nav.setAttribute('data-onlight', lum > 0.55 ? '1' : '0');
        }
      };

      onScroll = function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          update();
        });
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      window.addEventListener('load', update);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          if (!cancelled) update();
        });
      }
      var ladder = [120, 400, 900, 1800];
      for (var lt = 0; lt < ladder.length; lt++) {
        timers.push(setTimeout(update, ladder[lt]));
      }
      update();
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      if (onScroll) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
      if (update) {
        window.removeEventListener('load', update);
      }
      for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
      timers = [];
      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    });
    return teardown;
  }

  /* ------------------------------------------------------------------ *
   * Product detail — thumbnail gallery.
   *
   * Variant RESOLUTION and price display are theme.js's job: <product-info>
   * fetches this same product URL on option change and swaps the price,
   * gallery and hidden id in via innerHTML (see ProductInfo.updateSource-
   * FromDestination / #ProductGallery-... in fok-product-detail.liquid).
   * That swap replaces #fok-pd-img-main and every .fok-pd__thumb outright,
   * which would silently kill any listeners bound directly to them.
   *
   * So this binds one delegated click listener on the stable `.fok-pd`
   * root instead of on the thumbnails themselves — it keeps working after
   * theme.js's re-render because the root it's attached to is never
   * replaced, only its gallery children are.
   * ------------------------------------------------------------------ */
  function initProductDetail(root) {
    root = root || document;
    var pds = root.querySelectorAll('.fok-pd');
    var bound = [];
    var observers = [];

    for (var p = 0; p < pds.length; p++) {
      (function (pd) {
        var onClick = function (ev) {
          var t = ev.target.closest('.fok-pd__thumb[data-full]');
          if (!t || !pd.contains(t)) return;
          var main = pd.querySelector('#fok-pd-img-main');
          if (main) main.src = t.getAttribute('data-full');
          var thumbs = pd.querySelectorAll('.fok-pd__thumb[data-full]');
          for (var j = 0; j < thumbs.length; j++) thumbs[j].removeAttribute('data-on');
          t.setAttribute('data-on', '1');
        };
        pd.addEventListener('click', onClick);
        bound.push({ el: pd, type: 'click', fn: onClick });

        // theme.js sets aria-busy only on the in-form button; mirror it onto
        // the sticky one so the mobile bar shows the same loading spinner.
        var add = pd.querySelector('.fok-pd__addbtn');
        var sticky = pd.querySelector('.fok-pd__stickybtn');
        if (add && sticky) {
          var mo = new MutationObserver(function () {
            if (add.getAttribute('aria-busy') === 'true') {
              sticky.setAttribute('aria-busy', 'true');
            } else {
              sticky.removeAttribute('aria-busy');
            }
          });
          mo.observe(add, { attributes: true, attributeFilter: ['aria-busy'] });
          observers.push(mo);
        }
      })(pds[p]);
    }

    return registerTeardown(function () {
      for (var b = 0; b < bound.length; b++) {
        bound[b].el.removeEventListener(bound[b].type, bound[b].fn);
      }
      bound = [];
      for (var o = 0; o < observers.length; o++) observers[o].disconnect();
      observers = [];
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. Hero video — generalised across the four page variants to target
   *    video[data-fok-video]. No FOK template emits that attribute yet
   *    (Task 14 adds the first hero video); zero matches is expected.
   *    Design source (body verbatim, generalised over one element instead
   *    of a hardcoded id): By-State.js _heroVideoInit (By-State.js:31-42),
   *    the variant also used byte-for-byte by The-Fears.js and Research.js
   *    and closely by Shop.js. Homepage-Redesign-v2.js's variant is the
   *    lone outlier (no defaultMuted/playsInline/controls, no retry, no
   *    loadeddata/canplay/click retries) and is NOT the source here —
   *    without playsInline, FOK hero videos would go fullscreen instead of
   *    playing inline on iOS Safari.
   * ------------------------------------------------------------------ */
  function initHeroVideo(root) {
    root = root || document;
    var hvTries = 0;
    var cancelled = false;
    var bound = []; /* {el, type, fn, opts} for every listener we attach, so teardown can remove them */

    function run() {
      if (cancelled) return;
      var videos = root.querySelectorAll('video[data-fok-video]');
      if (!videos.length) {
        hvTries += 1;
        if (hvTries < 12) setTimeout(run, 200);
        return;
      }

      var reduce = reducedMotion();

      for (var i = 0; i < videos.length; i++) {
        (function (v) {
          v.muted = true;
          v.defaultMuted = true;
          v.loop = true;
          v.playsInline = true;
          v.controls = false;
          v.setAttribute('muted', ''); // React boolean-attr non-reflection; harmless alongside defaultMuted

          if (reduce) {
            v.pause();
            return;
          }

          /* Deferred loading. The markup ships <source data-src> and no
             autoplay attribute, so the browser fetches nothing during first
             paint — only the poster. We promote data-src to src and call
             load() once the page is done, which is what stops 15MB of video
             competing with FCP (measured: FCP 4160ms -> target <1500ms).

             readyState is checked rather than trusting the load event: by the
             time fok.js runs, 'load' may already have fired, and a listener
             added afterwards never runs — the video would stay blank
             forever. */
          var promote = function () {
            var srcs = v.querySelectorAll('source[data-src]');
            var promoted = false;
            for (var s = 0; s < srcs.length; s++) {
              if (!srcs[s].getAttribute('src')) {
                srcs[s].setAttribute('src', srcs[s].getAttribute('data-src'));
                promoted = true;
              }
            }
            if (promoted) v.load(); /* required: <source> changes are ignored without it */
          };

          var play = function () {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          };

          /* Below-the-fold videos wait until they are near the viewport;
             above-the-fold ones start as soon as the page has loaded. Either
             way, nothing is fetched during first paint.

             An above-fold hero is "intersecting" from the very first
             IntersectionObserver callback, which can fire before the page
             has finished loading -- observing starts on DOMContentLoaded,
             well ahead of the load event. Gating on readyState too (not
             just intersection) keeps the fetch out of the load race for
             those heroes; below-fold videos are unaffected since the user
             has to scroll to reach them, by which point load has long since
             fired. */
          var start = function () {
            promote();
            play();
          };

          var startWhenLoaded = function () {
            if (document.readyState === 'complete') {
              start();
            } else {
              window.addEventListener('load', start, { once: true });
              bound.push({ el: window, type: 'load', fn: start, opts: { once: true } });
            }
          };

          if ('IntersectionObserver' in window) {
            var io = new IntersectionObserver(function (entries) {
              for (var e = 0; e < entries.length; e++) {
                if (entries[e].isIntersecting) {
                  io.disconnect();
                  startWhenLoaded();
                }
              }
            }, { rootMargin: '200px' });
            io.observe(v);
            bound.push({ el: v, type: '__io', fn: function () { io.disconnect(); }, opts: null });
          } else {
            startWhenLoaded();
          }

          var onLoadedData = function () {
            play();
          };
          var onCanPlay = function () {
            play();
          };
          var onClick = function () {
            play();
          };

          v.addEventListener('loadeddata', onLoadedData, { once: true });
          v.addEventListener('canplay', onCanPlay, { once: true });
          document.addEventListener('click', onClick, { once: true });

          bound.push({ el: v, type: 'loadeddata', fn: onLoadedData, opts: { once: true } });
          bound.push({ el: v, type: 'canplay', fn: onCanPlay, opts: { once: true } });
          bound.push({ el: document, type: 'click', fn: onClick, opts: { once: true } });
        })(videos[i]);
      }
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      for (var b = 0; b < bound.length; b++) {
        if (bound[b].type === '__io') { bound[b].fn(); continue; } /* IntersectionObserver, not a listener */
        bound[b].el.removeEventListener(bound[b].type, bound[b].fn, bound[b].opts);
      }
      bound = [];
    });
    return teardown;
  }

  /* ------------------------------------------------------------------ *
   * 5. CTA attention — Homepage-Redesign-v2.js / Shop.js _merchAttnInit
   *    (verified byte-identical between the two). Kept contract: our
   *    built markup uses [data-fok-attention] / .fok-attention-run on
   *    three elements (header #merch-cta-nav, mobile bar, footer), with
   *    CSS already defining "[data-fok-attention].fok-attention-run".
   *    Timing and replay semantics taken verbatim from the design: reflow
   *    force via offsetWidth, 7600ms clear, IntersectionObserver re-entry
   *    replay (threshold 0.4) keyed on #nav-hero, with the inView latch.
   * ------------------------------------------------------------------ */
  function initAttention(root) {
    root = root || document;
    if (reducedMotion()) {
      var noop = registerTeardown(function () {});
      return noop;
    }

    var maTries = 0;
    var maT = null;
    var io = null;
    var cancelled = false;

    function run() {
      if (cancelled) return;
      var btns = root.querySelectorAll('[data-fok-attention]');
      if (!btns.length) {
        maTries += 1;
        if (maTries < 12) setTimeout(run, 200);
        return;
      }

      function play() {
        for (var i = 0; i < btns.length; i++) {
          var btn = btns[i];
          btn.classList.remove('fok-attention-run');
          void btn.offsetWidth;
          btn.classList.add('fok-attention-run');
        }
        clearTimeout(maT);
        maT = setTimeout(function () {
          for (var i = 0; i < btns.length; i++) {
            btns[i].classList.remove('fok-attention-run');
          }
        }, 7600);
      }

      play(); // on load

      /* replay when the header scrolls back into view (once per re-entry) */
      var hdr = document.getElementById('nav-hero') || btns[0];
      var inView = true;
      io = new IntersectionObserver(
        function (entries) {
          for (var e = 0; e < entries.length; e++) {
            var entry = entries[e];
            if (entry.isIntersecting && !inView) {
              inView = true;
              play();
            } else if (!entry.isIntersecting) {
              inView = false;
            }
          }
        },
        { threshold: 0.4 }
      );
      io.observe(hdr);
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      clearTimeout(maT);
      if (io) {
        io.disconnect();
        io = null;
      }
    });
    return teardown;
  }

  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ *
   * 6. Product carousel — the merch thumbnail strip.
   *    Design source: Homepage-Redesign-v2.js _heroScroll (also Research.js
   *    and The-Fears.js, which are the same behaviour). The design hardcodes
   *    getElementById('hero-thumbs'); we resolve each button's own target via
   *    data-carousel-target so several instances can coexist on one page.
   *    Step is 210 (Homepage/Research). The-Fears uses 208 — a 2px difference
   *    not worth branching on.
   * ------------------------------------------------------------------ */
  function initCarousels(root) {
    root = root || document;
    var bound = [];
    var cancelled = false;
    var tries = 0;

    function run() {
      if (cancelled) return;
      var btns = root.querySelectorAll('[data-carousel-prev],[data-carousel-next]');
      if (!btns.length) {
        tries += 1;
        if (tries < 12) setTimeout(run, 200);
        return;
      }
      for (var i = 0; i < btns.length; i++) {
        (function (btn) {
          var dir = btn.hasAttribute('data-carousel-next') ? 1 : -1;
          var onClick = function () {
            var id = btn.getAttribute('data-carousel-target');
            var track = id ? document.getElementById(id) : null;
            if (!track) return;
            track.scrollBy({ left: dir * 210, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          };
          btn.addEventListener('click', onClick);
          bound.push({ el: btn, fn: onClick });
        })(btns[i]);
      }
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      for (var b = 0; b < bound.length; b++) {
        bound[b].el.removeEventListener('click', bound[b].fn);
      }
      bound = [];
    });
    return teardown;
  }

  /* ------------------------------------------------------------------ *
   * 7. Testimonials — the "Voices of the Movement" STATE carousel
   *    (design-hp-voices.html, #nav-voices). Unlike initCarousels above
   *    (which scrolls a track), this swaps content in place: exactly one
   *    whole slide ([data-testi-slide], fok-testimonials-slide.liquid's
   *    block root) carries [data-active] at a time. Fix Round 1 replaced
   *    the original per-field approach (toggling individual
   *    quote/name/role/photo nodes) with whole-slide toggling, since
   *    theme blocks only render via {% content_for 'blocks' %} and their
   *    settings aren't readable by iterating section.blocks — the
   *    section can no longer interleave each block's fields itself, so
   *    each slide's fragments travel together under one root instead.
   *    Design source: Homepage-Redesign-v2.js:3 (_testiSet) and :468-478
   *    (TESTI, tCounter, testiPrev/testiNext, tFade). Constants carried
   *    across verbatim:
   *      - the 40ms fade-back delay (_testiSet's setTimeout(..., 40))
   *      - wrap-both-directions arithmetic, generalised from the design's
   *        literal 3 to the section's actual slide count: prev is
   *        (i + n - 1) % n, next is (i + 1) % n
   *      - tCounter format "N / total" (1-based), read here from the
   *        actual number of [data-testi-slide] elements rather than a
   *        hardcoded 3
   *    No slide server-renders as initially active (the block has no way
   *    to know its own position when rendered via content_for), so this
   *    initialiser marks slide 0 active itself on first run, before any
   *    prev/next click — the single source of truth for "what's active"
   *    stays in this file instead of being split with server markup.
   *    Fix Round 2: nav buttons and the counter now render once PER SLIDE
   *    (moved into fok-testimonials-slide.liquid so each slide is the
   *    design's complete, self-contained two-column structure — see that
   *    file's comment). So there are N prev/next button pairs and N
   *    counters per panel, one set per slide, of which only the active
   *    slide's are visible/interactive at a time; every slide's own
   *    prev/next pair is wired identically (clicking any of them moves the
   *    same shared current index), and setActive() writes the counter
   *    text into every slide's counter node (cheap — N is always small —
   *    and means whichever slide becomes active already has the right
   *    text with no per-slide branching required).
   *    Multiple instances: every query is scoped to root, and each
   *    section root carries its own {{ section.id }}-derived id set by
   *    the Liquid template, exactly as fok-product-carousel.liquid does.
   * ------------------------------------------------------------------ */
  function initTestimonials(root) {
    root = root || document;
    var tries = 0;
    var cancelled = false;
    var bound = [];
    var timers = [];

    function run() {
      if (cancelled) return;
      var panels = root.querySelectorAll('[data-fok-testimonials]');
      if (!panels.length) {
        tries += 1;
        if (tries < 12) setTimeout(run, 200);
        return;
      }

      for (var p = 0; p < panels.length; p++) {
        (function (panel) {
          var slideNodes = panel.querySelectorAll('[data-testi-slide]');
          var n = slideNodes.length;
          if (!n) return;

          var fadeEls = panel.querySelectorAll('[data-testi-fade]');
          var counterEls = panel.querySelectorAll('[data-testi-counter]');
          var current = 0;
          var fadeTimer = null;

          function setActive(i) {
            for (var s = 0; s < n; s++) {
              if (s === i) {
                slideNodes[s].setAttribute('data-active', '');
              } else {
                slideNodes[s].removeAttribute('data-active');
              }
            }
            var counterText = (i + 1) + ' / ' + n;
            for (var c = 0; c < counterEls.length; c++) counterEls[c].textContent = counterText;
          }

          function goTo(i) {
            clearTimeout(fadeTimer);
            current = i;
            if (reducedMotion()) {
              setActive(current);
              return;
            }
            for (var e = 0; e < fadeEls.length; e++) fadeEls[e].style.opacity = '0.2';
            fadeTimer = setTimeout(function () {
              setActive(current);
              for (var e2 = 0; e2 < fadeEls.length; e2++) fadeEls[e2].style.opacity = '1';
            }, 40);
            timers.push(fadeTimer);
          }

          /* No slide self-marks active on render; establish slide 0 as
           * active immediately (no fade — this is initial state, not a
           * transition) so the carousel has a defined starting point. */
          setActive(0);

          var prevBtns = panel.querySelectorAll('[data-testi-prev]');
          var nextBtns = panel.querySelectorAll('[data-testi-next]');

          for (var pb = 0; pb < prevBtns.length; pb++) {
            (function (btn) {
              var onPrev = function () {
                goTo((current + n - 1) % n);
              };
              btn.addEventListener('click', onPrev);
              bound.push({ el: btn, fn: onPrev });
            })(prevBtns[pb]);
          }
          for (var nb = 0; nb < nextBtns.length; nb++) {
            (function (btn) {
              var onNext = function () {
                goTo((current + 1) % n);
              };
              btn.addEventListener('click', onNext);
              bound.push({ el: btn, fn: onNext });
            })(nextBtns[nb]);
          }
        })(panels[p]);
      }
    }

    run();

    var teardown = registerTeardown(function () {
      cancelled = true;
      for (var b = 0; b < bound.length; b++) {
        bound[b].el.removeEventListener('click', bound[b].fn);
      }
      bound = [];
      for (var t = 0; t < timers.length; t++) clearTimeout(timers[t]);
      timers = [];
    });
    return teardown;
  }

  /* Mobile nav dismissal. NOT from the design — the design's <details> closes
     only via its own summary. Closing on an outside click (and on Escape) is
     the behaviour people expect from a burger menu; requested by the user
     2026-09-09, registered in DESIGN-DEVIATIONS.md.

     Listeners are on document because the click that should close the panel
     lands outside it by definition. Clicks inside the panel and on the
     summary are left alone, so the toggle keeps working normally. */
  function initMobileNav(root) {
    root = root || document;

    function closeOpen(except) {
      var navs = root.querySelectorAll('details.fok-mnav[open]');
      for (var i = 0; i < navs.length; i++) {
        if (navs[i] !== except) navs[i].removeAttribute('open');
      }
    }

    function onPointerDown(e) {
      var navs = root.querySelectorAll('details.fok-mnav[open]');
      if (!navs.length) return;
      for (var i = 0; i < navs.length; i++) {
        /* Inside the open menu — including its summary, which toggles itself. */
        if (navs[i].contains(e.target)) return;
      }
      closeOpen(null);
    }

    function onKeyDown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      var navs = root.querySelectorAll('details.fok-mnav[open]');
      if (!navs.length) return;
      closeOpen(null);
      /* Return focus to the toggle so keyboard users are not stranded. */
      var summary = navs[0].querySelector('summary');
      if (summary && typeof summary.focus === 'function') summary.focus();
    }

    /* pointerdown rather than click: a click that starts inside the panel and
       ends outside it should not count as an outside dismissal. */
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);

    return registerTeardown(function () {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    });
  }

  /* By State filter drawer.

     The <details class="fok-m-filts"> must carry `open` in the markup: above
     767px it is display:contents, and a CLOSED <details> keeps its
     non-summary children out of normal flow, which collapses the filter row
     into a 360px wrapped stack instead of one 48px row. So desktop and
     tablet need it permanently expanded.

     At <=767px that same `open` means the absolutely-positioned dropdown is
     already covering the state grid on load, before any interaction. This
     closes it for the mobile tier only, and re-opens it when the viewport
     crosses back above the breakpoint so the desktop row is never left
     collapsed. User interaction below the breakpoint is untouched - the
     summary toggles as normal. */
  function initFilterDrawer(root) {
    root = root || document;

    var MOBILE = '(max-width: 767px)';

    function sync() {
      var isMobile = window.matchMedia(MOBILE).matches;
      var drawers = root.querySelectorAll('details.fok-m-filts');
      for (var i = 0; i < drawers.length; i++) {
        var d = drawers[i];
        if (isMobile) {
          /* Only on the crossing, so a user who opened it stays in control. */
          if (d.getAttribute('data-fok-mobile') !== '1') {
            d.setAttribute('data-fok-mobile', '1');
            d.removeAttribute('open');
          }
        } else if (d.getAttribute('data-fok-mobile') === '1' || !d.hasAttribute('open')) {
          d.removeAttribute('data-fok-mobile');
          d.setAttribute('open', '');
        }
      }
    }

    function openDrawers() {
      var open = [];
      var drawers = root.querySelectorAll('details.fok-m-filts[open]');
      for (var i = 0; i < drawers.length; i++) open.push(drawers[i]);
      return open;
    }

    /* Dismissal applies to the mobile tier only: above it the element is a
       permanently-expanded display:contents wrapper, and closing it would
       collapse the desktop filter row. */
    function closeAll() {
      var open = openDrawers();
      for (var i = 0; i < open.length; i++) open[i].removeAttribute('open');
      return open.length > 0;
    }

    function onPointerDown(e) {
      if (!window.matchMedia(MOBILE).matches) return;
      var open = openDrawers();
      if (!open.length) return;
      for (var i = 0; i < open.length; i++) {
        /* Inside the drawer — including its summary, which toggles itself. */
        if (open[i].contains(e.target)) return;
      }
      closeAll();
    }

    /* A filter option applies immediately, so the drawer has done its job and
       should get out of the way of the results it just changed. Bound on the
       drawer rather than the button so it still fires for options rendered
       later, and after the filter's own listener has run. */
    function onOptionClick(e) {
      if (!window.matchMedia(MOBILE).matches) return;
      var target = e.target;
      var opt = target && typeof target.closest === 'function'
        ? target.closest('[data-fok-states-filter]')
        : null;
      if (!opt) return;
      var drawer = opt.closest('details.fok-m-filts');
      if (drawer) drawer.removeAttribute('open');
    }

    function onKeyDown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      if (!window.matchMedia(MOBILE).matches) return;
      var open = openDrawers();
      if (!open.length) return;
      var summary = open[0].querySelector('summary');
      closeAll();
      /* Return focus to the toggle so keyboard users are not stranded. */
      if (summary && typeof summary.focus === 'function') summary.focus();
    }

    sync();

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('click', onOptionClick, false);
    document.addEventListener('keydown', onKeyDown, true);

    var mq = window.matchMedia(MOBILE);
    var hasMqListener = typeof mq.addEventListener === 'function';
    if (hasMqListener) mq.addEventListener('change', sync);
    else mq.addListener(sync); /* Safari < 14 */

    return registerTeardown(function () {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('click', onOptionClick, false);
      document.removeEventListener('keydown', onKeyDown, true);
      if (hasMqListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    });
  }

  /* Auto-hide the docked social rail while scrolling down, bring it back on
     scrolling up. MOBILE ONLY (<=767): at tablet the rail stays put, and on
     desktop it is a vertical left rail that never docks.

     Direction alone is too twitchy, so distance in the current direction is
     accumulated and the rail only moves once it passes a threshold — further
     to hide than to reveal, so a reveal feels eager and a hide does not fire
     on a jiggle. The accumulator resets whenever direction changes.

     The reserved body padding is deliberately NOT collapsed with the bar:
     removing it mid-scroll would shift the page under the reader. */
  function initSocialRail(root) {
    root = root || document;

    var rail = root.querySelector('#socialbar');
    if (!rail) return;

    var HIDE_AFTER = 20;   /* px of downward travel before tucking */
    var SHOW_AFTER = 20;   /* px of upward travel before returning */
    var TOP_ZONE   = 320;  /* always shown this close to the top */
    var BOTTOM_PAD = 4;    /* treat "within 4px of the end" as the bottom */

    var mq = window.matchMedia('(max-width: 767px)');
    var lastY = window.pageYOffset || 0;
    var travel = 0;
    var tucked = false;
    var raf = null;

    function setTucked(next) {
      if (next === tucked) return;
      tucked = next;
      rail.classList.toggle('fok-socialrail--tucked', next);
    }

    function update() {
      var y = window.pageYOffset || 0;

      /* Off the mobile tier: never tucked, and the accumulator is irrelevant. */
      if (!mq.matches) {
        setTucked(false);
        lastY = y;
        travel = 0;
        return;
      }

      var dy = y - lastY;
      lastY = y;

      /* Reverse of direction starts the count again. */
      if ((dy > 0) !== (travel > 0)) travel = 0;
      travel += dy;

      if (y <= TOP_ZONE) { setTucked(false); return; }

      /* At the very bottom the rail must be reachable, whatever the travel. */
      var atBottom =
        window.innerHeight + y >= document.documentElement.scrollHeight - BOTTOM_PAD;
      if (atBottom) { setTucked(false); return; }

      if (travel >= HIDE_AFTER) { setTucked(true); travel = 0; }
      else if (travel <= -SHOW_AFTER) { setTucked(false); travel = 0; }
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; update(); });
    }

    function onBreakpoint() {
      travel = 0;
      lastY = window.pageYOffset || 0;
      update();
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    var hasMq = typeof mq.addEventListener === 'function';
    if (hasMq) mq.addEventListener('change', onBreakpoint);
    else mq.addListener(onBreakpoint); /* Safari < 14 */

    return registerTeardown(function () {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (hasMq) mq.removeEventListener('change', onBreakpoint);
      else mq.removeListener(onBreakpoint);
      rail.classList.remove('fok-socialrail--tucked');
    });
  }

  function initAll(root) {
    root = root || document;
    initReveal(root);
    initCounters(root);
    initScrollNav(root);
    initHeroVideo(root);
    initProductDetail(root);
    initAttention(root);
    initCarousels(root);
    initTestimonials(root);
    initMobileNav(root);
    initFilterDrawer(root);
    initSocialRail(root);
  }

  window.FOK = {
    initReveal: initReveal,
    initCounters: initCounters,
    initScrollNav: initScrollNav,
    initHeroVideo: initHeroVideo,
    initProductDetail: initProductDetail,
    initAttention: initAttention,
    initCarousels: initCarousels,
    initTestimonials: initTestimonials,
    initMobileNav: initMobileNav,
    initFilterDrawer: initFilterDrawer,
    initSocialRail: initSocialRail,
    initAll: initAll,
    teardownAll: teardownAll,
    prefersReducedMotion: prefersReducedMotion
  };

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    teardownAll();
    initAll(document);
  });
  document.addEventListener('shopify:section:unload', teardownAll);

  /* Floating cart button: show it whenever the cart is non-empty. The initial
     state comes from Liquid, so this only has to follow live changes.
     cart-count handles its own badge; it cannot hide the wrapping button. */
  (function cartFab() {
    var fab = document.getElementById('fok-cart-fab');
    if (!fab || !window.theme || !theme.pubsub) return;

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event.cart || event.cart.errors) return;
      fab.setAttribute('data-show', event.cart.item_count > 0 ? '1' : '0');
    });
  })();
})(window, document);
